// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Link as RouterLink, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, Box, TextField, InputAdornment } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import CircularProgress from '@mui/material/CircularProgress';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import paths from 'paths';
import { getAccessUsers } from 'actions/access';

import UserInviteForm from './UserInviteForm';
import PensionsInfoInviteForm from './PensionsInfoInviteForm';

const matchesUserSearch = (user, search) => {
    const term = search.trim().toLowerCase();

    if (!term) {
        return true;
    }

    const values = [
        user?.name,
        user?.email,
        user?.userId,
        user?.sharingId,
        user?.initiator?.email,
        user?.initiator?.userId,
    ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());

    return values.some((value) => value.includes(term));
};

const Users = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { organisationId } = useParams();
    const [groupedUsers, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [showPensionsInfoModal, setShowPensionsInfoModal] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    useEffect(() => {
        const fetch = async () => {
            const users = (await dispatch(getAccessUsers(organisationId))).filter((x) => !!x.email || !!x.userId);
            setUsers(groupUsersByPartner(users));
            setLoading(false);
        };
        fetch();
    }, [dispatch, organisationId]);

    const filteredGroupedUsers = useMemo(
        () => groupedUsers
            .map((users) => users.filter((user) => matchesUserSearch(user, search)))
            .filter((users) => users.length > 0),
        [groupedUsers, search],
    );

    useEffect(() => {
        setPage(0);
    }, [filteredGroupedUsers.length]);

    const handleOpenRequestModal = () => {
        setShowRequestModal(true);
    };

    const handleCloseRequestModal = () => {
        setShowRequestModal(false);
    };

    const handleOpenPensionsInfoModal = () => {
        setShowPensionsInfoModal(true);
    };

    const handleClosePensionsInfoModal = () => {
        setShowPensionsInfoModal(false);
    };

    const organisation = useSelector(state => state.auth.user.organisations.find(org => org.organisationId === organisationId));
    const showPenlyAccessInvite = location.search === '?showRequestModal=true' || organisation.enabledFeatures?.includes('PenlyAccessInvite');

    const getUserPath = (userId) => paths.penlyAccessUser.getPath(organisationId, userId);
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pagedGroupedUsers = filteredGroupedUsers.slice(startIndex, endIndex);

    return (
        <>
            { loading && (
                <div className="App-section-loading">
                    <CircularProgress  color="primary" size={ 40 } />
                </div>
            ) }
            { !loading && (
                <>
                    { showPenlyAccessInvite &&
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mb: 2 }}>
                            <Button variant="contained" onClick={ handleOpenRequestModal }>
                                Inviter til Penly data deling
                            </Button>
                            <Button variant="contained" onClick={ handleOpenPensionsInfoModal }>
                                Inviter til PensionsInfo deling
                            </Button>
                        </Box>
                    }

                    <Box sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Søg brugere..."
                            value={ search }
                            onChange={ (event) => setSearch(event.target.value) }
                            autoComplete="off"
                            sx={{
                                width: 360,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                paddingLeft: 1,
                                paddingRight: 1,
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.23)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0, 0, 0, 0.87)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">Navn</TableCell>
                                    <TableCell align="left">Sidst opdateret</TableCell>
                                    <TableCell align="left">Email</TableCell>
                                    <TableCell align="left">BrugerId</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <>
                                { pagedGroupedUsers.length === 0 && (
                                    <TableBody>
                                        <TableRow>
                                            <TableCell colSpan={ 5 } align="center">Ingen brugere matcher søgningen.</TableCell>
                                        </TableRow>
                                    </TableBody>
                                ) }
                                { pagedGroupedUsers.map((users, i) => (
                                    <TableBody key={ users[0]?.userId || `${startIndex}-${i}` }>
                                        { users.length > 1 && (
                                            <TableRow style={{ position: 'relative' }}>
                                                <td><GroupIcon sx={{ position: 'absolute', top: 37, left: 1 }} /></td>
                                            </TableRow>
                                        ) }
                                        { users.toReversed().map((user) => (
                                            <TableRow key={ user.userId }>
                                                <TableCell align="left">{ user.name }</TableCell>
                                                <TableCell align="left">{ new Date(user.sharingUpdatedAt).toLocaleDateString('da-DK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) }</TableCell>
                                                <TableCell align="left">{ user.email }</TableCell>
                                                <TableCell align="left">{ user.userId ?? 'Afventer deling' }</TableCell>
                                                <TableCell sx={{ p: 0 }} align="right">
                                                    { user.userId && (
                                                        <Button component={ RouterLink } to={{ pathname: getUserPath(user.userId) }} variant="contained">Åben</Button>
                                                    ) }
                                                </TableCell>
                                            </TableRow>
                                        )) }
                                        { users.length > 1 && (
                                            <TableRow>
                                                <TableCell colSpan={ 5 }></TableCell>
                                            </TableRow>
                                        ) }
                                    </TableBody>
                                )) }
                            </>
                        </Table>
                        <TablePagination
                            component="div"
                            count={ filteredGroupedUsers.length }
                            page={ page }
                            rowsPerPage={ rowsPerPage }
                            labelRowsPerPage="Rækker pr. side"
                            onPageChange={ (event, newPage) => setPage(newPage) }
                            onRowsPerPageChange={ (event) => {
                                setRowsPerPage(Number(event.target.value));
                                setPage(0);
                            } }
                            rowsPerPageOptions={ [10, 25, 50] }
                        />
                    </TableContainer>
                </>
            ) }
            <Modal open={ showRequestModal } onClose={ handleCloseRequestModal }>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        overflowY: 'scroll',
                        maxHeight: '95vh',
                    }}
                >
                    <UserInviteForm onClose={ handleCloseRequestModal } />
                </Box>
            </Modal>
            <Modal open={ showPensionsInfoModal } onClose={ handleClosePensionsInfoModal }>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        overflowY: 'scroll',
                        maxHeight: '95vh',
                        minWidth: '800px',
                    }}
                >
                    <PensionsInfoInviteForm onClose={ handleClosePensionsInfoModal } />
                </Box>
            </Modal>
        </>
    );
};

export default Users;

const groupUsersByPartner = (users) => {
    const groupedUsers = [];
    // first find all users who has been invited by a partner
    users
        .filter((user) => !!user.initiator?.userId)
        .forEach((user) => {
            groupedUsers.push([user]);
        });
    // then take all users who has not been invited by a partner, and add them to a group and or create a 1-person group
    users
        .filter((user) => !user.initiator?.userId)
        .forEach((user) => {
            const existingGroup = groupedUsers.find((group) => group.some((u) => u.initiator?.userId === user.userId && user.userId));
            if (existingGroup) {
                existingGroup.push(user);
            } else {
                groupedUsers.push([user]);
            }
        });

    return groupedUsers;
};
